import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, GraduationCap, Calendar, User, FileText, Clock, DollarSign } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Training {
  id: string;
  employee_id: string;
  training_name: string;
  training_type: string | null;
  provider: string | null;
  start_date: string | null;
  end_date: string | null;
  duration_hours: number | null;
  cost_amount: number | null;
  status: string;
  grade_obtained: string | null;
  completion_certificate_url: string | null;
  created_at: string;
  updated_at: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  department: string;
}

export const TrainingManagement = () => {
  const { toast } = useToast();
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [trainingDialog, setTrainingDialog] = useState(false);
  const [editingTraining, setEditingTraining] = useState<Training | null>(null);

  const [trainingForm, setTrainingForm] = useState({
    employee_id: '',
    training_name: '',
    training_type: '',
    provider: '',
    start_date: '',
    end_date: '',
    duration_hours: '',
    cost_amount: '',
    status: 'Planifié',
    grade_obtained: '',
    completion_certificate_url: ''
  });

  useEffect(() => {
    fetchTrainings();
    fetchEmployees();
  }, []);

  const fetchTrainings = async () => {
    try {
      const { data, error } = await supabase
        .from('employee_trainings')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;
      setTrainings(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des formations:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les formations",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, first_name, last_name, position, department')
        .eq('status', 'active')
        .order('first_name', { ascending: true });

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des employés:', error);
    }
  };

  const handleTrainingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const trainingData = {
        ...trainingForm,
        duration_hours: trainingForm.duration_hours ? parseInt(trainingForm.duration_hours) : null,
        cost_amount: trainingForm.cost_amount ? parseFloat(trainingForm.cost_amount) : null,
        start_date: trainingForm.start_date || null,
        end_date: trainingForm.end_date || null,
        completion_certificate_url: trainingForm.completion_certificate_url || null
      };

      if (editingTraining) {
        const { error } = await supabase
          .from('employee_trainings')
          .update(trainingData)
          .eq('id', editingTraining.id);

        if (error) throw error;

        toast({
          title: "Succès",
          description: "Formation mise à jour avec succès"
        });
      } else {
        const { error } = await supabase
          .from('employee_trainings')
          .insert([trainingData]);

        if (error) throw error;

        toast({
          title: "Succès",
          description: "Nouvelle formation créée avec succès"
        });
      }

      fetchTrainings();
      resetForm();
      setTrainingDialog(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder la formation",
        variant: "destructive"
      });
    }
  };

  const handleEditTraining = (training: Training) => {
    setEditingTraining(training);
    setTrainingForm({
      employee_id: training.employee_id,
      training_name: training.training_name,
      training_type: training.training_type || '',
      provider: training.provider || '',
      start_date: training.start_date || '',
      end_date: training.end_date || '',
      duration_hours: training.duration_hours?.toString() || '',
      cost_amount: training.cost_amount?.toString() || '',
      status: training.status,
      grade_obtained: training.grade_obtained || '',
      completion_certificate_url: training.completion_certificate_url || ''
    });
    setTrainingDialog(true);
  };

  const handleDeleteTraining = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette formation ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('employee_trainings')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Formation supprimée avec succès"
      });

      fetchTrainings();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la formation",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setEditingTraining(null);
    setTrainingForm({
      employee_id: '',
      training_name: '',
      training_type: '',
      provider: '',
      start_date: '',
      end_date: '',
      duration_hours: '',
      cost_amount: '',
      status: 'Planifié',
      grade_obtained: '',
      completion_certificate_url: ''
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Terminé':
        return <Badge className="bg-green-100 text-green-800">{status}</Badge>;
      case 'En cours':
        return <Badge className="bg-blue-100 text-blue-800">{status}</Badge>;
      case 'Planifié':
        return <Badge className="bg-yellow-100 text-yellow-800">{status}</Badge>;
      case 'Annulé':
        return <Badge className="bg-red-100 text-red-800">{status}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTypeColor = (type: string | null) => {
    if (!type) return 'bg-gray-100 text-gray-800';
    
    switch (type.toLowerCase()) {
      case 'technique':
        return 'bg-blue-100 text-blue-800';
      case 'sécurité':
        return 'bg-red-100 text-red-800';
      case 'management':
        return 'bg-purple-100 text-purple-800';
      case 'soft skills':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Employé inconnu';
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return 'Non spécifié';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-sm text-muted-foreground">Chargement des formations...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <GraduationCap className="h-6 w-6 text-primary" />
              <span>Gestion des formations</span>
            </CardTitle>
            <CardDescription>
              Planifiez et suivez les formations des employés
            </CardDescription>
          </div>
          <Dialog open={trainingDialog} onOpenChange={setTrainingDialog}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm}
                className="bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle formation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingTraining ? 'Modifier la formation' : 'Nouvelle formation'}
                </DialogTitle>
                <DialogDescription>
                  {editingTraining ? 'Modifiez les détails de la formation' : 'Créez une nouvelle formation pour un employé'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleTrainingSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="employee_id">Employé *</Label>
                    <Select 
                      value={trainingForm.employee_id} 
                      onValueChange={(value) => setTrainingForm({...trainingForm, employee_id: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un employé" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((employee) => (
                          <SelectItem key={employee.id} value={employee.id}>
                            {employee.first_name} {employee.last_name} - {employee.position}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="training_name">Nom de la formation *</Label>
                    <Input
                      id="training_name"
                      placeholder="Ex: Formation sécurité au travail"
                      value={trainingForm.training_name}
                      onChange={(e) => setTrainingForm({...trainingForm, training_name: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="training_type">Type de formation</Label>
                    <Select 
                      value={trainingForm.training_type} 
                      onValueChange={(value) => setTrainingForm({...trainingForm, training_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Technique">Technique</SelectItem>
                        <SelectItem value="Sécurité">Sécurité</SelectItem>
                        <SelectItem value="Management">Management</SelectItem>
                        <SelectItem value="Soft Skills">Soft Skills</SelectItem>
                        <SelectItem value="Compliance">Compliance</SelectItem>
                        <SelectItem value="Autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="provider">Organisme de formation</Label>
                    <Input
                      id="provider"
                      placeholder="Ex: Centre de formation XYZ"
                      value={trainingForm.provider}
                      onChange={(e) => setTrainingForm({...trainingForm, provider: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Statut</Label>
                    <Select 
                      value={trainingForm.status} 
                      onValueChange={(value) => setTrainingForm({...trainingForm, status: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Planifié">Planifié</SelectItem>
                        <SelectItem value="En cours">En cours</SelectItem>
                        <SelectItem value="Terminé">Terminé</SelectItem>
                        <SelectItem value="Annulé">Annulé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Date de début</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={trainingForm.start_date}
                      onChange={(e) => setTrainingForm({...trainingForm, start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">Date de fin</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={trainingForm.end_date}
                      onChange={(e) => setTrainingForm({...trainingForm, end_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="duration_hours">Durée (heures)</Label>
                    <Input
                      id="duration_hours"
                      type="number"
                      min="0"
                      placeholder="Ex: 40"
                      value={trainingForm.duration_hours}
                      onChange={(e) => setTrainingForm({...trainingForm, duration_hours: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cost_amount">Coût (USD)</Label>
                    <Input
                      id="cost_amount"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Ex: 1500.00"
                      value={trainingForm.cost_amount}
                      onChange={(e) => setTrainingForm({...trainingForm, cost_amount: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="grade_obtained">Note obtenue</Label>
                    <Input
                      id="grade_obtained"
                      placeholder="Ex: A, 85%, Réussi"
                      value={trainingForm.grade_obtained}
                      onChange={(e) => setTrainingForm({...trainingForm, grade_obtained: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="completion_certificate_url">URL du certificat</Label>
                  <Input
                    id="completion_certificate_url"
                    type="url"
                    placeholder="https://example.com/certificat.pdf"
                    value={trainingForm.completion_certificate_url}
                    onChange={(e) => setTrainingForm({...trainingForm, completion_certificate_url: e.target.value})}
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setTrainingDialog(false)}>
                    Annuler
                  </Button>
                  <Button type="submit">
                    {editingTraining ? 'Mettre à jour' : 'Créer la formation'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {trainings.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <GraduationCap className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Aucune formation trouvée</p>
            <p className="text-sm">Créez votre première formation d'employé</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employé</TableHead>
                <TableHead>Formation</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Durée</TableHead>
                <TableHead>Coût</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trainings.map((training) => (
                <TableRow key={training.id}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{getEmployeeName(training.employee_id)}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{training.training_name}</p>
                      {training.provider && (
                        <p className="text-sm text-muted-foreground">{training.provider}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {training.training_type && (
                      <Badge className={getTypeColor(training.training_type)}>
                        {training.training_type}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {training.start_date && (
                        <div className="flex items-center space-x-1 mb-1">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span>Début: {new Date(training.start_date).toLocaleDateString('fr-FR')}</span>
                        </div>
                      )}
                      {training.end_date && (
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span>Fin: {new Date(training.end_date).toLocaleDateString('fr-FR')}</span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {training.duration_hours ? (
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{training.duration_hours}h</span>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Non spécifié</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-1">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{formatCurrency(training.cost_amount)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(training.status)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditTraining(training)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Modifier
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteTraining(training.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};